import React, { useState, useEffect, forwardRef, useRef } from "react";
import PropTypes from "prop-types";

import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import { isElementFocused } from "@/shared/utils/dom";

import IconButton from "../IconButton/IconButton";
import Input from "../Input/Input";

import EgwWebFont from "../../assets/EgwWebFont";

import styles from "./SearchField.module.scss";

const SearchField = forwardRef(({
  className,
  inputClassName,
  isIconClearVisible,
  isIconSearchVisible,
  isIconSearchEnabled,
  placeholder,
  value,
  onSearch,
  onClear,
  onInputFocus,
  onInputClick,
  onInputBlur,
  onInputKeyDown,
  onChange,
  isWithDebounce,

  size,

  autoFocus = true,
  required,

  slotBefore,
  slotAfter,
}, refInput) => {
  const [search, setSearch] = useState(value || "");
  const { t } = useSharedTranslation();
  const refIsLastMouseDownWithFocusedInput = useRef(false);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  useEffect(() => {
    let timeout;

    if (isWithDebounce) {
      timeout = setTimeout(() => {
        onChange(search);
      }, 500);
    } else {
      onChange(search);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [search, isWithDebounce]);

  const handleSearch = (event) => {
    event.preventDefault();

    if (search.length && onSearch) {
      onSearch(search);
    }
  };

  const handleClear = (event) => {
    setSearch("");
    if (onClear) {
      onClear(event);
    }
  };

  return (
    <Input
      tagRoot="form"
      rootProps={{
        onSubmit: handleSearch,
        role: "search",
      }}

      ref={refInput}
      className={className}
      inputClassName={inputClassName}
      variant={Input.variant.search}
      size={size}
      value={search}
      data-focus={autoFocus ? "true" : "false"}
      required={required}
      placeholder={placeholder ?? t("search")}
      autoCapitalize="false"
      autoCorrect="false"
      onKeyDown={onInputKeyDown}
      onChange={(event) => {
        setSearch(event.target.value.replace(/“/g, "\""));
      }}
      onFocus={(e) => {
        if (onInputFocus) {
          onInputFocus();
        }
      }}
      onClick={onInputClick}
      onMouseDown={(e) => {
        refIsLastMouseDownWithFocusedInput.current = isElementFocused(e.target);
      }}
      onMouseUp={(e) => {
        if (!refIsLastMouseDownWithFocusedInput.current) {
          setTimeout(() => {
            e.target.select();
          }, 0)
        }
      }}
      onBlur={onInputBlur}

      slotBefore={slotBefore && (
        <div className={styles["actions"]}>
          {slotBefore}
        </div>
      )}
      slotAfter={(
        <div className={styles["actions"]}>
          {!!search && (<IconButton
            icon={EgwWebFont.close}
            className={styles["btn-clear"]}
            title={t("clear")}
            onClick={handleClear}
          />)}
          {isIconSearchVisible && (<IconButton
            tabIndex={-1}
            icon={EgwWebFont.search}
            className={styles["btn-search"]}
            title={t("search")}
            onClick={(event) => {
              if (isIconSearchEnabled) {
                handleSearch(event);
              }
            }}
          />)}
          {slotAfter}
        </div>
      )}
    />
  );
});

SearchField.defaultProps = {
  isIconClearVisible: true,
  isIconSearchVisible: true,
  isIconSearchEnabled: true,
  onChange: () => {},
};

SearchField.propTypes = {
  isWithDebounce: PropTypes.bool,
  isIconClearVisible: PropTypes.bool,
  isIconSearchVisible: PropTypes.bool,
  isIconSearchEnabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  onInputFocus: PropTypes.func,
  onInputClick: PropTypes.func,
  onInputBlur: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default SearchField;
